import { useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, ZoomControl, useMap } from 'react-leaflet';
import styles from './HomeMapCanvas.module.css';
import 'leaflet/dist/leaflet.css';

function MapCamera({ markers, selectedId, fitRequest, isActive }) {
  const map = useMap();
  const fitted = useRef(null);
  const focused = useRef(null);
  const selected = markers.find(marker => marker.id === selectedId);
  const pointsKey = JSON.stringify(markers.map(marker => [marker.id, marker.lat, marker.lng]));
  const fitKey = JSON.stringify([pointsKey, fitRequest]);

  useEffect(() => {
    if (!isActive) return;
    const element = map.getContainer();
    let frame;
    const resize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => map.invalidateSize({ animate: false, pan: false }));
    };
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    resize();
    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, [map, isActive]);

  useEffect(() => {
    if (!isActive) return;
    const frame = requestAnimationFrame(() => {
      map.invalidateSize({ animate: false, pan: false });
      if (fitted.current !== fitKey) {
        if (markers.length) map.fitBounds(markers.map(marker => [marker.lat, marker.lng]), {
          padding: [40, 40], maxZoom: 14, animate: false,
        });
        fitted.current = fitKey;
      }
      const focusKey = selected ? JSON.stringify([selected.id, selected.lat, selected.lng]) : null;
      if (selected && focused.current !== focusKey) {
        map.setView([selected.lat, selected.lng], Math.max(map.getZoom(), selected.mode === 'assets' ? 12 : 8), { animate: false });
      }
      focused.current = focusKey;
    });
    return () => cancelAnimationFrame(frame);
  }, [map, markers, selected, fitKey, isActive]);
  return null;
}

export default function HomeMapCanvas({ markers, selectedId, onSelect, fitRequest, isActive }) {
  const center = useMemo(() => markers.length ? [markers[0].lat, markers[0].lng] : [20, 0], [markers]);
  return <div className={styles.canvas} aria-label="Fleet location map">
    <MapContainer center={center} zoom={2} className={styles.leaflet} zoomControl={false} scrollWheelZoom>
      <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      {markers.map(marker => <CircleMarker key={marker.id} center={[marker.lat, marker.lng]}
        radius={marker.id === selectedId ? 11 : 7}
        pathOptions={{
          color: marker.id === selectedId ? 'var(--color-grey)' : 'var(--color-deep-secondary)',
          weight: marker.id === selectedId ? 3 : 2,
          fillColor: marker.id === selectedId ? 'var(--color-primary)' : 'var(--color-secondary)',
          fillOpacity: 1,
        }}
        eventHandlers={{ click: () => onSelect(marker.id) }}>
        <Tooltip direction="top">{marker.label}</Tooltip>
      </CircleMarker>)}
      <MapCamera markers={markers} selectedId={selectedId} fitRequest={fitRequest} isActive={isActive} />
      <ZoomControl position="bottomright" />
    </MapContainer>
  </div>;
}